import React, { useCallback } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, H1, H2, ListRow, Separator, Text } from '@betterment/samba-react';
import { Navigate, useNavigate } from 'react-router-dom';

import { SecLendingEnrollmentInviteTerms } from './SecLendingEnrollmentInviteTerms';

import { UserLevelSettingsDocument } from '../edit-user-level-settings/graphql/query.userLevelSettings.generated';
import { useEditUserLevelSettings } from '../edit-user-level-settings/hooks/useEditUserLevelSettings';
import { SecuritiesLendingStatus } from '../graphql/schema';
import { IdempotencyProviderWithFeatureRegistry } from '../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { SecLending } from '../Shared/components-v2/user-level-settings/components/SettingsControls';
import UserLevelSettings from '../Shared/components-v2/user-level-settings/UserLevelSettings';
import { WorkflowLayout } from '../Shared/layout/WorkflowLayout';

export const SecLendingEnrollmentInviteContent: React.FC = () => {
  const navigate = useNavigate();
  const { handleUpdate, loading } = useEditUserLevelSettings('/');
  const { data } = useGraphQLQuery(UserLevelSettingsDocument);

  const isAlreadyEnrolled = data?.userLevelSettings.securityLending.status === SecuritiesLendingStatus.Enrolled;

  const handleEnrollment = useCallback(() => {
    void handleUpdate(true);
  }, [handleUpdate]);

  if (!data) return <FullPageLoader />;
  if (isAlreadyEnrolled) return <Navigate to="/" />;

  return (
    <WorkflowLayout pageTitle="Securities lending" workflowTitle="Securities lending" onClose={() => navigate('/')}>
      <H1 variant="title400" css={{ mb: 'space.300' }}>
        Confirm your opt in to securities lending.
      </H1>
      <UserLevelSettings controls={[SecLending(true, true)]} />
      <Box
        css={{
          display: 'grid',
          gap: 'space.200',
          padding: 'space.200',
          border: '1px solid',
          borderColor: 'border.weak',
          borderRadius: 'medium',
          mt: '-space.100',
        }}
      >
        <ListRow css={{ padding: 'space.0' }}>
          <ListRow.LeadingContent>
            <Box css={{ display: 'grid' }}>
              <H2 variant="title100">Securities lending terms</H2>
            </Box>
          </ListRow.LeadingContent>
          <ListRow.TrailingContent>
            <Box css={{ display: 'grid', gap: 'space.100', justifyItems: 'center' }} />
          </ListRow.TrailingContent>
        </ListRow>
        <Separator />
        <SecLendingEnrollmentInviteTerms />
      </Box>
      <Text css={{ my: 'space.300' }}>
        By clicking confirm, I acknowledge that I have read and agree to the terms above.
      </Text>

      <Button onClick={handleEnrollment} variant="solidPrimary" css={{ width: 'fit-content' }} disabled={loading}>
        Confirm
      </Button>
    </WorkflowLayout>
  );
};

export const SecLendingEnrollmentInvite: React.FC = () => (
  <IdempotencyProviderWithFeatureRegistry featureName="editUserLevelSettings">
    <SecLendingEnrollmentInviteContent />
  </IdempotencyProviderWithFeatureRegistry>
);
